import { BUILD_STATUSES, BuildStatus, isBoundForAnalysis } from '@cadstrom/cadstrom-shared';

import type { RevisionStatus, RevisionWithFiles } from '@cadstrom/cadstrom-shared';

/** The fields a revision's next action is resolved from. */
export type ActionableRevision = Pick<RevisionWithFiles, 'status' | 'hasUploadedFiles' | 'automationMode'>;

/**
 * A revision's single next action. Each kind names what the control offers, so
 * a revision with nothing to show yet never reads as one whose results are
 * ready.
 */
export enum RevisionActionKind {
	/** Waiting on the first file; nothing runs until one lands. */
	Upload = 'upload',
	/** The BOM review gate is open and the customer's corrections are what moves it on. */
	BomReview = 'bom_review',
	/** Work is queued or running, or will be dispatched without anyone picking it up. */
	ViewProgress = 'view_progress',
	/** Parked at the analysis gate for a person to start the run. */
	StartAnalysis = 'start_analysis',
	/** Parked at the analysis gate until the organization adds design review credit itself. */
	AddCredit = 'add_credit',
	/** Parked at the analysis gate until sales arranges design review credit for the organization. */
	ContactSales = 'contact_sales',
	/** The checks have finished and their findings are there to read. */
	ViewResults = 'view_results',
	/** The revision failed; the action leads to the reason. */
	ViewError = 'view_error',
}

/**
 * Resolve a revision's next-action kind from its status.
 *
 * A revision at the analysis gate resolves by automation mode: one the
 * automation will dispatch reads as progress, and one parked for a person reads
 * as the start of the run. Whether that person is the customer is settled
 * downstream, by `useRevisionAction`.
 *
 * @param revision Revision to resolve the action for.
 * @returns The action kind, or `null` when nothing is actionable.
 */
export function getRevisionActionKind(revision: ActionableRevision): RevisionActionKind | null {
	switch (revision.status) {
		case BuildStatus.Pending: {
			return revision.hasUploadedFiles ? RevisionActionKind.ViewProgress : RevisionActionKind.Upload;
		}
		case BuildStatus.BomAwaitingReview: {
			return RevisionActionKind.BomReview;
		}
		case BuildStatus.Validating:
		case BuildStatus.Processing:
		case BuildStatus.ProcessingBom:
		case BuildStatus.ProcessingDesign:
		case BuildStatus.AnalysisComplete: {
			return RevisionActionKind.ViewProgress;
		}
		case BuildStatus.ReadyToAnalyze: {
			return isBoundForAnalysis(revision.status, revision.automationMode) ?
					RevisionActionKind.ViewProgress
				:	RevisionActionKind.StartAnalysis;
		}
		case BuildStatus.CreditRequired: {
			return RevisionActionKind.AddCredit;
		}
		case BuildStatus.Complete: {
			return RevisionActionKind.ViewResults;
		}
		case BuildStatus.Error: {
			return RevisionActionKind.ViewError;
		}
		case BuildStatus.Abandoned:
		case BuildStatus.TimedOut:
		case BuildStatus.Unknown: {
			return null;
		}
	}
}

/** The current revision (most recent) plus the older revisions behind it. */
export interface RevisionTimeline {
	current: RevisionWithFiles | undefined;
	history: RevisionWithFiles[];
}

/**
 * Split revisions into the current (most-recently-created) one and the older
 * history behind it, newest first.
 *
 * @param revisions Revisions for the project.
 * @returns The current revision (or undefined when there are none) and the
 *   remaining history, both ordered most-recent first.
 */
export function toRevisionTimeline(revisions: RevisionWithFiles[]): RevisionTimeline {
	const ordered = [...revisions].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
	const [current, ...history] = ordered;
	return { current, history };
}

/**
 * Statuses an operator may set manually. Excludes statuses that are only ever set by
 * the system (`pending`, `timed_out`, `credit_required`, `unknown`) or by the abandon
 * flow (`abandoned`).
 */
export const SELECTABLE_STATUSES: RevisionStatus[] = BUILD_STATUSES.filter(
	(status) =>
		status !== BuildStatus.Pending &&
		status !== BuildStatus.TimedOut &&
		status !== BuildStatus.CreditRequired &&
		status !== BuildStatus.Unknown &&
		status !== BuildStatus.Abandoned,
);

/**
 * Human-readable file count for a revision.
 *
 * @param count Number of files.
 * @returns e.g. "1 file" or "5 files".
 */
export function formatFileCount(count: number): string {
	return `${count} file${count === 1 ? '' : 's'}`;
}
